import type { Content } from "@amzn/ads-discovery-theme-service-typescript-client";
import type {
    InsightType,
    TemplateMetadata,
} from "@amzn/d16-g-client-deck-builder-typescript-client";
import type { AsyncStatus } from "@react-hookz/web";
import { produce } from "immer";

import type { AdditionalSubmitInfo } from "../components/aquilaThumbsFeedback/AquilaThumbsFeedback";
import type { AquilaThumbsFeedbackPlacement } from "../types/aquila";
import type { CardDetail } from "../types/card";
import type { DownloadAsset } from "../types/common";
import type { ContentUser } from "../types/content";
import type {
    FeedbackInsightIdentifier,
    FeedbackMetadata,
    RatingOption,
} from "../types/feedback";
import { InsightPreviewFileFormats } from "../types/insightPreviewFileFormats";

import { zustandStore } from "./zustand.helpers";

export interface Compliance {
    isCompliant: boolean;
    status: string | null;
}

export interface FeedbackOptions {
    thumbsUp: RatingOption;
    thumbsDown: RatingOption;
}

export interface FeedbackProps {
    feedbackOptions: FeedbackOptions;
    insightIdentifier: FeedbackInsightIdentifier;
    metadata: FeedbackMetadata;
    aquilaThumbsFeedbackSubmit?: (
        identifier: string,
        insightType: InsightType,
        placement: AquilaThumbsFeedbackPlacement,
        additionalInfo: AdditionalSubmitInfo,
    ) => void;
}

export interface InsightDetailsInfo {
    details: CardDetail[];
    title: string;
    previewFileFormat: InsightPreviewFileFormats;
    outputFileFormats?: string[];
    identifier: string;
    feedback?: FeedbackProps;
    insightType?: InsightType;
    previewAsset?: DownloadAsset;
    downloadAsset?: DownloadAsset;
    description?: string;
    previewLoading?: boolean;
    previewGenerating?: boolean;
    downloadLoading?: boolean;
    compliance?: Compliance;
    additionalDetails?: JSX.Element;
    contentUsers?: ContentUser[];
    content?: Content;
    // To be removed
    handleEditInsight?: () => void;
    handleShareInsight?: () => void;
    isShareEnabled?: boolean;
    template?: TemplateMetadata;
}

export const defaultInfo: InsightDetailsInfo = {
    details: [],
    title: "",
    identifier: "",
    previewAsset: {
        downloadUrl: "",
        content: {
            bucket: "",
            key: "",
        },
    },
    previewLoading: false,
    previewFileFormat: InsightPreviewFileFormats.PDF,
};

export interface InsightDetailsState {
    info: InsightDetailsInfo;
    open?: boolean;
    asyncStatus?: AsyncStatus;
}

export const initialState: InsightDetailsState = {
    info: defaultInfo,
    open: false,
    asyncStatus: "not-executed",
};

export interface InsightDetailsActions {
    setInsightDetails: (insightDetails: InsightDetailsState) => void;
    setDownloadInfo: (downloadAsset: DownloadAsset) => void;
    setAsyncStatus: (status: AsyncStatus) => void;
    resetState: () => void;
}

export const { store: insightDetailsStore } = zustandStore<
    InsightDetailsState,
    InsightDetailsActions
>("InsightDetails", initialState, (set: any, get: any) => ({
    setInsightDetails: (insightDetails: InsightDetailsState) => {
        const prev = get();
        return set(() => ({ ...prev, ...insightDetails }));
    },
    setDownloadInfo: (downloadAsset: DownloadAsset) => {
        const prev = get();
        return set(() => ({
            ...prev,
            info: {
                ...prev.info,
                downloadAsset,
            },
        }));
    },
    setAsyncStatus: (status: AsyncStatus) =>
        set(() => {
            const state: InsightDetailsState = get();
            return produce(state, (draft) => {
                draft.asyncStatus = status;
                if (status === "loading") {
                    draft.open = true;
                }
                return draft;
            });
        }),
    resetState: () => set(() => ({ ...initialState })),
}));
